import React, { useEffect, useState } from 'react';
import { 
  Star, 
  Filter, 
  RefreshCw,
  Search,
  ChevronDown,
  MessageSquare,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import ReviewTable from '../../components/admin/marketplace/ReviewTable';
import useAdminStore from '../../store/adminStore';
import { toast } from 'react-hot-toast';

const AdminReviewsPage = () => {
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    status: '',
    minRating: '',
    search: '',
  });

  const [showFilters, setShowFilters] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    averageRating: 0
  });

  const {
    marketplaceReviews,
    marketplaceReviewsPagination,
    isLoading,
    getAllReviews,
  } = useAdminStore();

  useEffect(() => {
    loadReviews();
  }, [filters.page, filters.limit, filters.status, filters.minRating]);

  useEffect(() => {
    calculateStats();
  }, [marketplaceReviews]);

  const loadReviews = async () => {
    try {
      await getAllReviews(filters);
    } catch (error) {
      console.error('Failed to load reviews:', error);
    }
  };

  const calculateStats = () => {
    const reviews = marketplaceReviews || [];
    const total = reviews.length;
    const pending = reviews.filter(r => r.status === 'PENDING').length;
    const approved = reviews.filter(r => r.status === 'APPROVED').length;
    const rejected = reviews.filter(r => r.status === 'REJECTED').length;
    const avgRating = approved > 0 
      ? reviews.filter(r => r.status === 'APPROVED').reduce((sum, r) => sum + r.rating, 0) / approved
      : 0;

    setStats({
      total,
      pending,
      approved,
      rejected,
      averageRating: parseFloat(avgRating.toFixed(1))
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, page: 1 });
    loadReviews();
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const handleRefresh = () => {
    loadReviews();
    toast.success('Reviews refreshed');
  };

  const handleViewReview = (review) => {
    setSelectedReview(review);
    toast.success('Review details loaded');
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (marketplaceReviewsPagination?.totalPages || 1)) {
      setFilters({ ...filters, page: newPage });
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    setFilters({ ...filters, limit: newLimit, page: 1 });
  };

  const getRatingDistribution = () => {
    const reviews = marketplaceReviews?.filter(r => r.status === 'APPROVED') || [];
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    
    reviews.forEach(review => {
      distribution[review.rating]++;
    });

    return distribution;
  };

  const ratingDist = getRatingDistribution();
  const formatNumber = (num) => new Intl.NumberFormat().format(num || 0);

  const total = marketplaceReviewsPagination?.total || 0;
  const currentPage = marketplaceReviewsPagination?.page || 1;
  const totalPages = marketplaceReviewsPagination?.totalPages || 1;
  const limit = marketplaceReviewsPagination?.limit || 20;
  const startIndex = (currentPage - 1) * limit + 1;
  const endIndex = Math.min(currentPage * limit, total);

  return (
    <div className="min-h-screen bg-[#E8ECF5] p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-[#E8ECF5] rounded-xl p-3 shadow-neomorph mb-3 border border-primary-100/50">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <div className="flex items-center space-x-1.5 mb-0.5">
                <div className="w-1 h-5 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full" />
                <h1 className="text-lg font-bold text-gray-800">Review Moderation</h1>
              </div>
              <p className="text-xs text-gray-500 ml-2.5">Manage and moderate marketplace reviews</p>
            </div>
            
            <div className="mt-2 md:mt-0 flex items-center space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="bg-white rounded-lg px-3 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs font-medium text-gray-700 flex items-center border border-gray-100"
              >
                <Filter className="h-3.5 w-3.5 mr-1.5" />
                Filters
                <ChevronDown className={`h-3.5 w-3.5 ml-1.5 transform transition-transform ${
                  showFilters ? 'rotate-180' : ''
                }`} />
              </button>
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="bg-white rounded-lg px-3 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs font-medium text-gray-700 flex items-center border border-gray-100"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
          <div className="bg-white rounded-lg p-3 shadow-neomorph border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-gray-600 mb-0.5">Total Reviews</p>
                <p className="text-base font-bold text-gray-800">{formatNumber(stats.total)}</p>
              </div>
              <div className="p-1.5 bg-gradient-to-br from-primary-500 to-primary-600 rounded-md shadow-md">
                <MessageSquare className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 shadow-neomorph border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-gray-600 mb-0.5">Pending</p>
                <p className="text-base font-bold text-yellow-600">{formatNumber(stats.pending)}</p>
              </div>
              <div className="p-1.5 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-md shadow-md">
                <Clock className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 shadow-neomorph border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-gray-600 mb-0.5">Approved</p>
                <p className="text-base font-bold text-green-600">{formatNumber(stats.approved)}</p>
              </div>
              <div className="p-1.5 bg-gradient-to-br from-green-500 to-green-600 rounded-md shadow-md">
                <CheckCircle className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 shadow-neomorph border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-gray-600 mb-0.5">Rejected</p>
                <p className="text-base font-bold text-red-600">{formatNumber(stats.rejected)}</p>
              </div>
              <div className="p-1.5 bg-gradient-to-br from-red-500 to-red-600 rounded-md shadow-md">
                <XCircle className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 shadow-neomorph border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-gray-600 mb-0.5">Avg Rating</p>
                <p className="text-base font-bold text-purple-600">{stats.averageRating}</p>
              </div>
              <div className="p-1.5 bg-gradient-to-br from-purple-500 to-purple-600 rounded-md shadow-md">
                <Star className="h-3.5 w-3.5 text-white fill-current" />
              </div>
            </div>
          </div>
        </div>

        {/* Rating Distribution */}
        <div className="bg-white rounded-xl p-3 shadow-neomorph border border-gray-100 mb-3">
          <h3 className="text-xs font-semibold text-gray-800 mb-3">Rating Distribution</h3>
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map(rating => {
              const count = ratingDist[rating];
              const percentage = stats.approved > 0 ? (count / stats.approved) * 100 : 0;
              
              return (
                <div key={rating} className="flex items-center space-x-2.5">
                  <div className="flex items-center w-16">
                    <span className="text-[10px] font-medium text-gray-700">{rating} star</span>
                    <Star className="h-3 w-3 text-yellow-400 fill-current ml-0.5" />
                  </div>
                  <div className="flex-1">
                    <div className="h-1.5 bg-[#E8ECF5] rounded-full overflow-hidden shadow-neomorph-inset">
                      <div 
                        className="h-full bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                  <div className="w-12 text-right">
                    <span className="text-[10px] font-medium text-gray-600">{formatNumber(count)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white rounded-xl p-3 shadow-neomorph border border-gray-100 mb-3">
            <form onSubmit={handleSearch} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">Search</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                      placeholder="Search reviews..."
                      className="block w-full pl-8 pr-2 py-1.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                    />
                    <Search className="absolute left-2.5 top-1.5 h-3.5 w-3.5 text-gray-400" />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="block w-full px-2 py-1.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  >
                    <option value="">All</option>
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">Minimum Rating</label>
                  <select
                    value={filters.minRating}
                    onChange={(e) => handleFilterChange('minRating', e.target.value)}
                    className="block w-full px-2 py-1.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  >
                    <option value="">Any Rating</option>
                    <option value="5">5 Stars</option>
                    <option value="4">4+ Stars</option>
                    <option value="3">3+ Stars</option>
                    <option value="2">2+ Stars</option>
                    <option value="1">1+ Stars</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setFilters({
                      page: 1,
                      limit: 20,
                      status: '',
                      minRating: '',
                      search: '',
                    });
                  }}
                  className="bg-white rounded-lg px-3 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs font-medium text-gray-700 border border-gray-100"
                >
                  Clear
                </button>
                <button
                  type="submit"
                  className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-3 py-1.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-xs font-medium"
                >
                  Apply
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Reviews Table */}
        <div className="bg-white rounded-xl p-3 shadow-neomorph border border-gray-100 mb-3">
          <ReviewTable
            reviews={marketplaceReviews || []}
            pagination={marketplaceReviewsPagination}  // ← ADD THIS
            onView={handleViewReview}
            onRefresh={loadReviews}
            onPageChange={handlePageChange}            // ← ADD THIS
            isLoading={isLoading}
          />
        </div>

        {/* Pagination Controls */}
        {marketplaceReviewsPagination && marketplaceReviewsPagination.total > 0 && (
          <div className="flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-600">Show:</label>
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="px-1.5 py-1 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-xs text-gray-500">per page</span>
            </div>

            <p className="text-xs text-gray-500 bg-white rounded-lg px-2.5 py-1 shadow-neomorph-inset">
              Showing <span className="font-medium text-gray-800">{startIndex}</span> to{' '}
              <span className="font-medium text-gray-800">{endIndex}</span> of{' '}
              <span className="font-medium text-gray-800">{total}</span> reviews
            </p>

            <div className="flex items-center space-x-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || isLoading}
                className={`p-1.5 rounded-lg transition-all ${
                  currentPage === 1 || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="First"
              >
                <ChevronsLeft className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
                className={`p-1.5 rounded-lg transition-all ${
                  currentPage === 1 || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Previous"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>

              <div className="flex items-center space-x-0.5">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-7 h-7 rounded-lg text-xs font-medium transition-all ${
                        currentPage === pageNum
                          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30'
                          : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isLoading}
                className={`p-1.5 rounded-lg transition-all ${
                  currentPage === totalPages || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Next"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || isLoading}
                className={`p-1.5 rounded-lg transition-all ${
                  currentPage === totalPages || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Last"
              >
                <ChevronsRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default AdminReviewsPage;